import React, { useEffect, useRef, useState } from 'react';
import './collaborators-ind.styles.scss';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import CollabListItem from './collab-list-item.component';
import FoundCollaboratorModal from './found-collaborator-modal.component';
import { set } from 'date-fns';
import { getEmailContent } from '@/utils/getEmailContent';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import useAnalytics from '@/utils/useAnalytics';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

const CollaboratorsIndividual = ({
  gameData,
  setCollabSystem,
  orgs,
  removeCollaborator,
  pendingInvites
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const [alertMessage, setAlertMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collabs, setCollabs] = useState([]);
  const [foundCollaborator, setFoundCollaborator] = useState(null);
  const [addCollabLoading, setAddCollabLoading] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const { trackEvent, trackException } = useAnalytics();
  const regsRef = useRef({});
  const approveRef = useRef(null);
  const storage = getStorage();

  useEffect(() => {
    if (!gameData) return;
    getGameCollaborators();
  }, [gameData])

  async function getGameCollaborators() {
    console.log('getting game regs: ' + gameData.gameId)
    const regs = await UseCloudFunction(
      'getPremiumCollaborators',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId
      }
    )
    if (regs.error) {
      setAlertMessage(`Error getting collaborators: ${regs.errorMessage}`);
      return;
    }
    console.log(regs);
    setCollabs(regs.collabArr);
    regsRef.current = regs.regs;
  }

  async function findPotentialCollaborator(isPremium) {
    console.log('finding potential collab, isPremium: ' + isPremium)
    const emailInput = document.getElementById('collaborator-email');
    if (!emailInput || emailInput.value.trim() === '') {
      setAlertMessage('Please enter a valid email address to find a collaborator.');
      return;
    }
    const email = emailInput.value.toLowerCase().trim();
    const res = await UseCloudFunction(
      'getPotentialPremiumCollaborator',
      {
        'email': email,
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage(`No potential collaborator found for that email.`);
      trackException('No potential collaborator found for requested email');
      return;
    }
    setFoundCollaborator(res.profile);
    trackEvent(
      'potential_individual_collaborator_found', 
      'Game Manager',
      `Found potential collaborator`,
      null,
      membership
    );
  }

  async function confirmAddCollaborator(role) {
    console.log('confirming add collabator: ' + role);
    setAddCollabLoading(true);

    let gameIcon = null;
    if (
      gameData.icon &&
      typeof gameData.icon === 'string' &&
      gameData.icon.includes('storage:')
    ) {
      const path = gameData.icon.split('storage:')[1];
      const imageRef = ref(storage, path);
      const url = await getDownloadURL(imageRef);
      gameIcon = url;
    } else if (gameData.icon && gameData.icon.includes('http')) {
      gameIcon = gameData.icon;
    } else {
      gameIcon = `https://app.gamably.com${gameData.icon}`;
    }

    // create invite email
    const emailContent = getEmailContent(
      'gameManagerInvite',
      {
        'email': foundCollaborator.email,
        'inviteAppId': foundCollaborator.appId,
        'appId': userProfile.appId,
        'gameName': gameData.gameName,
        'userName': `${foundCollaborator.fName} ${foundCollaborator.lName}`,
        'fName': foundCollaborator.fName,
        'lName': foundCollaborator.lName,
        'gameIcon': gameIcon,
        'role': role,
        'managerName': `${userProfile.fName} ${userProfile.lName}`,
        'managerAppId': userProfile.appId,
      }
    );

    const res = await UseCloudFunction(
      'inviteUserToGame',
      {
        "emailContent": emailContent,
        "gameId": gameData.gameId,
        "gameName": gameData.gameName,
        "gameIcon": gameIcon,
        "gamePath": gameData.path,
        "managerName": `${userProfile.fName} ${userProfile.lName}`,
        "managerAppId": userProfile.appId,
      }
    )
    if (res.error) {
      setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
      setAddCollabLoading(false);
      trackException('Error inviting collaborator: ' + res.error);
      return;
    }
    setAlertMessage('Settings saved successfully!')
    setAddCollabLoading(false);
    setFoundCollaborator(null);
    trackEvent(
      'collaborator_invited_from_individual', 
      'Game Manager',
      `Invited collaborator to game`,
      null,
      membership
    )
  }

  function removeAllCollaborators() {
    setConfirmMessage('Are you sure you want to remove all existing collaborators? ' +
      'This action will allow you to switch to inviting Organization ' +
      'members if you are part of an organization. All existing individual collaborators ' +
      'will be removed. This action cannot be undone.');
    approveRef.current = async () => {
      setLoading(true)
      const res = await UseCloudFunction(
        'removeExistingCollaborators',
        {
          'gameId': gameData.gameId,
          'appId': userProfile.appId
        }
      )
      console.log(res);
      if (res.error) {
        setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
        trackException('Error removing collaborators: ' + res.error);
        return;
      }
      setCollabSystem(null);
      setLoading(false);
      trackEvent(
        'all_collaborators_removed_from_individual', 
        'Game Manager',
        `Removed all collaborators from game`,
        null,
        membership
      )
    }
  }

  return (
    <div className='collaborators-ind'>
      <hr />
      <div className='header-with-button'>
        <h3>
          <ImageContainer src="/images/icons/premium.png" className='icon' />
          Invite Premium Users
        </h3>
        {
          (orgs) &&
          <button
            className='g-button small-btn'
            onClick={() => removeAllCollaborators()}>
            Remove All Collaborators
          </button>
        }
      </div>
      <div className="g-space-1"></div>
      <div className='g-list-item collab-list-div grey-background'>
        <div className="g-space-0-5"></div>
        <div className='input-row'>
          <div className='field'>
            <label>Enter email address here</label>
            <input
              type='text'
              placeholder='Collaborator Email Address'
              id='collaborator-email' />
          </div>
          <button
            className='g-button '
            onClick={() => findPotentialCollaborator(true)} >
            Find
            <FontAwesomeIcon icon={faMagnifyingGlass} className='icon' />
          </button>
        </div>
        <div className="g-space-2"></div>
        <b>Existing Collaborators</b>
        <div className="g-space-0-5"></div>
        <div className='collab-list'>
          {
            (collabs.length > 0)
              ?
              collabs.sort((a, b) => a.order > b.order ? 1 : -1)
                .map(c => (
                  <CollabListItem
                    key={c.appId}
                    collab={c}
                    removeCollaborator={removeCollaborator} />
                ))
              :
              <div>No collaborators yet...</div>
          }
        </div>
        <div className="g-space-2"></div>
        <div>
          <b>Pending Invites</b>
          <div className='collab-list-div'>
            <div className="g-space-0-5"></div>
            <div className='collab-list'>
              {
                (pendingInvites && Object.keys(pendingInvites).length > 0)
                  ?
                  Object.values(pendingInvites)
                    .map((invite, index) => {
                      if (collabs.find(c => c.appId === invite.inviteAppId)) {
                        return null;
                      }
                      return (
                        <CollabListItem
                          key={invite.inviteAppId + index}
                          collab={invite}
                          removeCollaborator={removeCollaborator} />
                      )
                    })
                  :
                  <div>No pending invites...</div>
              }
            </div>
            <div className="g-space-0-5"></div>
          </div>
        </div>
      </div>
      <FoundCollaboratorModal
        show={!!foundCollaborator}
        cancel={() => setFoundCollaborator(null)}
        collabProfile={foundCollaborator}
        confirm={(e) => confirmAddCollaborator(e)}
        loading={addCollabLoading} />
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default CollaboratorsIndividual